/**
 * Lightweight markdown parser for ChatGPT messages
 * Handles: bold, italic, links, lists (no external dependencies)
 */

/**
 * Unescape HTML entities that may come from backend
 */
const unescapeHtml = (text: string): string => {
  const htmlEntities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#039;': "'",
    '&#39;': "'"
  };

  let result = text;

  // Unescape HTML entities
  Object.entries(htmlEntities).forEach(([entity, char]) => {
    result = result.replace(new RegExp(entity, 'g'), char);
  });

  // Unescape backslash-escaped characters (e.g., \" → ")
  result = result
    .replace(/\\"/g, '"') // \" → "
    .replace(/\\'/g, "'") // \' → '
    .replace(/\\\\/g, '\\'); // \\ → \ (must be last to avoid double-unescaping)

  return result;
};

/**
 * Escape HTML special characters to prevent XSS
 */
const escapeHtml = (text: string): string => {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
};

/**
 * Parse inline markdown: bold, italic, links, emails
 * CRITICAL: Extract links/emails BEFORE escaping HTML to preserve special chars
 */
const parseInline = (text: string): string => {
  // Store links and emails to prevent HTML escaping
  const replacements: Array<{ placeholder: string; html: string }> = [];
  let replacementIndex = 0;

  // Extract markdown links first: [text](url)
  let result = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, linkText, url) => {
    const placeholder = `__REPLACEMENT_${replacementIndex}__`;
    replacements.push({
      placeholder,
      html: `<a href="${url}" target="_blank" rel="noopener noreferrer" class="markdown-link">${escapeHtml(
        linkText
      )}</a>`
    });
    replacementIndex++;
    return placeholder;
  });

  // Extract email addresses: email@domain.com
  result = result.replace(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g, (email) => {
    const placeholder = `__REPLACEMENT_${replacementIndex}__`;
    replacements.push({
      placeholder,
      html: `<a href="mailto:${email}" class="markdown-link">${email}</a>`
    });
    replacementIndex++;
    return placeholder;
  });

  // Now escape HTML (but links/emails are protected)
  result = escapeHtml(result);

  // Bold: **text** → <strong>text</strong>
  result = result.replace(/\*\*([^*]+)\*\*/g, '<strong class="markdown-strong">$1</strong>');

  // Italic: *text* → <em>text</em>
  result = result.replace(/\*([^*]+)\*/g, '<em class="markdown-em">$1</em>');

  // Restore links and emails
  replacements.forEach(({ placeholder, html }) => {
    result = result.replace(placeholder, html);
  });

  return result;
};

/**
 * Parse markdown to HTML
 * Supports: bold, italic, links, lists, line breaks
 */
export const parseMarkdown = (content: string): string => {
  if (!content || content.trim() === '') {
    return '';
  }

  console.log('[parseMarkdown] INPUT:', content.substring(0, 100));

  try {
    // First unescape any HTML entities from backend (e.g., &amp; → &)
    const unescaped = unescapeHtml(content);
    const lines = unescaped.split('\n');
    const htmlLines: string[] = [];
    let listDepth = 0; // Track nesting level

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      // Empty line - close all lists
      if (trimmed === '') {
        while (listDepth > 0) {
          htmlLines.push('</ul>');
          listDepth--;
        }
        continue;
      }

      // Detect list item with indentation
      const listMatch = line.match(/^(\s*)- (.+)$/);
      if (listMatch) {
        const indent = listMatch[1].length;
        const itemText = listMatch[2];

        // Calculate depth (every 2 spaces = 1 level)
        const targetDepth = Math.floor(indent / 2) + 1;

        // Close deeper lists
        while (listDepth > targetDepth) {
          htmlLines.push('</ul>');
          listDepth--;
        }

        // Open new lists
        while (listDepth < targetDepth) {
          const level = listDepth + 1;
          htmlLines.push(`<ul class="markdown-list markdown-list-${level}">`);
          listDepth++;
        }

        htmlLines.push(`<li class="markdown-li">${parseInline(itemText)}</li>`);
        continue;
      }

      // Close all lists for non-list content
      while (listDepth > 0) {
        htmlLines.push('</ul>');
        listDepth--;
      }

      // Regular line (paragraph)
      htmlLines.push(`<p class="markdown-p">${parseInline(trimmed)}</p>`);
    }

    // Close remaining lists
    while (listDepth > 0) {
      htmlLines.push('</ul>');
      listDepth--;
    }

    const output = htmlLines.join('');
    console.log('[parseMarkdown] OUTPUT:', output.substring(0, 200));
    return output;
  } catch (error) {
    console.error('Markdown parsing error:', error);
    return content;
  }
};
